import { Component, Inject, OnDestroy, OnInit } from "@angular/core";

import { AbstractControl, FormArray, FormGroup } from "@angular/forms";
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { SelectionModel } from "@angular/cdk/collections";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatTableDataSource } from "@angular/material/table";

import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { ServiceRequestContactSelectionService } from "@tylertech/contact-module";
import { LgdFormControlFactory } from "@tylertech/lgd-reactive-forms";
import { Utilities } from "@tylertech/shared-module";
import { IContactMatch, IContactSelectionServiceRequest, IServiceRequestContactSelection } from "@tylertech/tyler-api-types";

export interface ContactMatchDialogData
{
    readonly requestId: string;
}

type TableColumn = "Select" | keyof IContactMatch;

@Component({
    selector: "contact-match-dialog-component",
    templateUrl: "./contactMatchDialog.tmpl.html",
    styleUrls: [
        "./contactMatchDialog.component.scss"
    ],
    standalone: false
})
export class ContactMatchDialogComponent implements OnDestroy, OnInit
{
    private readonly destructor$ = new Subject<void>();
    private readonly tableRowsDestructor = new Subject<void>();
    public readonly selectionManager: SelectionModel<IContactMatch>;

    readonly columnNames: ReadonlyArray<TableColumn> = [
        "Select", "Name", "Address", "Phone", "DriversLicense"
    ];

    readonly tableData: MatTableDataSource<IContactMatch>;

    readonly tableForm: FormGroup;

    isLoaded: boolean;
    requestCard: IContactSelectionServiceRequest;

    constructor(
        private readonly dialogRef: MatDialogRef<ContactMatchDialogComponent, string | undefined>,
        @Inject(MAT_DIALOG_DATA) private readonly data: ContactMatchDialogData,
        private commonDialogsService: CommonDialogsService,
        private serviceRequestContactSelectionService: ServiceRequestContactSelectionService,
    )
    {
        const initialSelection = [];
        const allowMultiSelect = false;
        this.selectionManager = new SelectionModel<IContactMatch>(allowMultiSelect, initialSelection);

        this.tableData = new MatTableDataSource<IContactMatch>();

        this.tableForm = new FormGroup({
            rows: new FormArray([])
        });
    }

    private get rowsArray(): FormArray<FormGroup>
    {
        return this.tableForm.get("rows") as FormArray;
    }

    get noData(): boolean { return (this.tableData.data?.length || 0) === 0; }

    ngOnDestroy(): void
    {
        this.tableRowsDestructor.next();
        this.tableRowsDestructor.complete();

        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        this.serviceRequestContactSelectionService.FetchMatchingContacts(this.requestId)
            .then(
                (result: IServiceRequestContactSelection) =>
                {
                    this.requestCard = result.ServiceRequest;
                    this.tableData.data = result.Contacts;
                    this.initializeTableRows(result.Contacts);

                    if (result.Contacts.length > 0)
                    {
                        this.selectionManager.setSelection(result.Contacts[0]);
                        this.rowsArray.controls[0].setValue({ selected: true }, { emitEvent: false });
                    }
                },
                () =>
                {
                    // TODO - We are already in a dialog context... DO NOT OPEN SECONDARY DIALOGS
                    this.commonDialogsService.showAlertMessage("Fetching contacts failed");
                }
            ).
            finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private subscribeTo<T = unknown>(observable: Observable<T>, subscriptionFn: (newValue: T) => void, destructor?: Subject<void>): void
    {
        observable.pipe(takeUntil(destructor || this.destructor$))
            .subscribe(x =>
            {
                subscriptionFn(x);
            });
    }

    private subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void, destructor?: Subject<void>): void
    {
        this.subscribeTo(control.valueChanges, subscriptionFn, destructor);
    }

    private initializeTableRows(values: Array<IContactMatch>): void
    {
        this.tableRowsDestructor.next();
        this.rowsArray.clear();

        this.tableData.data = values;
        this.tableData.data
            .forEach(
                r =>
                {
                    const selectRowControl = LgdFormControlFactory.checkbox();
                    const rowFormGroup = new FormGroup({ selected: selectRowControl });
                    this.rowsArray.push(rowFormGroup);

                    this.subscribeToValueChanges(selectRowControl, (_newValue: boolean) =>
                    {
                        this.selectionManager.toggle(r);
                    });
                });
    }

    private get requestId(): string
    {
        return this.data.requestId;
    }

    cancel(): void
    {
        this.dialogRef.close(null);
    }

    continue(): void
    {
        const selected = this.selectionManager.selected[0];
        if (!selected)
        {
            return;
        }

        this.dialogRef.close(
            selected.Id || Utilities.emptyGuid);
    }

    newContact(): void
    {
        this.dialogRef.close(Utilities.emptyGuid);
    }
}
